import type { ProductPublic } from '@dzseguidores/types';

export type ProductBadge = 'popular' | 'value' | 'starter' | null;

function priceNum(p: ProductPublic): number {
  return Number(p.promoPriceCents ?? p.priceCents);
}

function unitCost(p: ProductPublic): number {
  const qty = Math.max(p.minQty, 1);
  return priceNum(p) / qty;
}

/** Marca pacotes com sinais de conversão (recomendado / melhor custo / entrada). */
export function badgeForProduct(product: ProductPublic, peers: ProductPublic[]): ProductBadge {
  if (peers.length < 2) return peers.length === 1 ? 'popular' : null;

  const sameService = peers.filter(
    (p) => p.networkSlug === product.networkSlug && p.serviceTypeSlug === product.serviceTypeSlug,
  );
  const pool = sameService.length >= 2 ? sameService : peers;
  if (pool.length < 2) return null;

  const byUnit = [...pool].sort((a, b) => unitCost(a) - unitCost(b));
  const byPrice = [...pool].sort((a, b) => priceNum(a) - priceNum(b));
  const byQty = [...pool].sort((a, b) => b.minQty - a.minQty);

  if (byUnit[0]?.id === product.id && byQty[0]?.id === product.id) return 'value';
  if (byUnit[0]?.id === product.id) return 'value';
  if (byPrice[0]?.id === product.id) return 'starter';

  // Pacote do meio de preço = âncora “mais vendido” (efeito decoy / recommended)
  const mid = byPrice[Math.floor((byPrice.length - 1) / 2)];
  if (mid?.id === product.id) return 'popular';

  return null;
}

export function badgeLabel(badge: ProductBadge): string | null {
  if (badge === 'popular') return 'Mais vendido';
  if (badge === 'value') return 'Melhor custo';
  if (badge === 'starter') return 'Entrada';
  return null;
}

export function savingsCents(product: ProductPublic): number {
  if (!product.promoPriceCents) return 0;
  return Math.max(0, Number(product.priceCents) - Number(product.promoPriceCents));
}

export function serviceLabel(slug: string): string {
  if (slug === 'followers') return 'seguidores';
  if (slug === 'likes') return 'curtidas';
  return slug;
}

/** Cross-sell: mesmo network, outro service type, pacote de entrada. */
export function pickCrossSell(
  current: ProductPublic,
  catalog: ProductPublic[],
): ProductPublic | null {
  const others = catalog.filter(
    (p) =>
      p.id !== current.id &&
      p.networkSlug === current.networkSlug &&
      p.serviceTypeSlug !== current.serviceTypeSlug,
  );
  if (others.length === 0) {
    const same = catalog
      .filter((p) => p.id !== current.id && p.networkSlug === current.networkSlug)
      .sort((a, b) => priceNum(a) - priceNum(b));
    return same[0] ?? null;
  }
  return [...others].sort((a, b) => priceNum(a) - priceNum(b))[0] ?? null;
}

/** Order bump: pacote barato complementar (likes se comprou followers, etc.). */
export function pickOrderBump(
  cartProductIds: string[],
  catalog: ProductPublic[],
): ProductPublic | null {
  const inCart = new Set(cartProductIds);
  const candidates = catalog
    .filter((p) => !inCart.has(p.id))
    .sort((a, b) => priceNum(a) - priceNum(b));
  return candidates[0] ?? null;
}

export const TRUST_POINTS = [
  { title: 'PIX instantâneo', detail: 'Aprovação em segundos' },
  { title: 'Sem senha', detail: 'Só o link público' },
  { title: 'Garantia', detail: 'Reposição conforme pacote' },
  { title: 'Suporte BR', detail: 'Atendimento em português' },
] as const;
